import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { ApiError, apiFetch } from "./client";
import type {
  AuthProvidersResponse,
  CellDetailResponse,
  CellsResponse,
  CLITokensResponse,
  DeviceDecisionResponse,
  DeviceInfoResponse,
  LogoutResponse,
  MeResponse,
  PlanIntentRequest,
  PlanIntentResponse,
  PricingOfferResponse,
  RepositoriesResponse,
  SummaryResponse,
} from "./types.gen";

/**
 * Query keys, in one place.
 *
 * Invalidation is only reliable if every call site spells the key the same
 * way, and a typo produces a cache that silently never updates rather than an
 * error.
 */
export const queryKeys = {
  me: ["me"] as const,
  pricingOffer: (slug: string) => ["pricing-offer", slug] as const,
  authProviders: ["auth", "providers"] as const,
  device: (userCode: string) => ["device", userCode] as const,
  cliTokens: ["cli", "tokens"] as const,
  repositories: (slug: string) => ["repositories", slug] as const,
  cells: (slug: string, repoID: string, experiment: string) =>
    ["cells", slug, repoID, experiment] as const,
  summary: (slug: string, repoID: string) => ["summary", slug, repoID] as const,
  cellDetail: (slug: string, repoID: string, cellID: string) =>
    ["cell-detail", slug, repoID, cellID] as const,
};

/**
 * Builds the app's QueryClient.
 *
 * The retry policy is the part worth reading: a 401 or a 403 must NOT be
 * retried. React Query's default retries three times, which for an
 * unauthenticated user means three round trips before the app decides to show
 * the login screen — and on a rate-limited API, three requests it did not need
 * to make.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry(failureCount, error) {
          if (error instanceof ApiError) {
            if (error.status === 401 || error.status === 403 || error.status === 404) {
              return false;
            }
          }
          return failureCount < 2;
        },
        // The session and workspace list change rarely; refetching them on
        // every window focus is noise on the API and a flash of loading state
        // for the user.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * The authenticated user, their linked providers, and their workspaces.
 *
 * A 401 is a normal state, not an error to display: it means "not signed in".
 * Callers distinguish the two with `isUnauthenticated` rather than inspecting
 * the error themselves.
 */
export function useMe() {
  const query = useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => apiFetch<MeResponse>("/api/v1/me", { signal }),
  });

  const isUnauthenticated =
    query.error instanceof ApiError && query.error.isUnauthorized;

  return {
    ...query,
    isUnauthenticated,
    // A 401 is an answer, not a failure, so it must not surface as one.
    isError: query.isError && !isUnauthenticated,
  };
}

/**
 * Which sign-in buttons to render.
 *
 * Asking the server means a provider with no credentials configured is not
 * offered, instead of showing a button that fails after the user clicks it.
 */
export function useAuthProviders() {
  return useQuery({
    queryKey: queryKeys.authProviders,
    queryFn: ({ signal }) =>
      apiFetch<AuthProvidersResponse>("/api/v1/auth/providers", { signal }),
    // Providers change when the deployment changes, i.e. never within a
    // session.
    staleTime: Infinity,
  });
}

/**
 * Ends the session.
 *
 * The whole cache is cleared rather than just the `me` key: anything fetched
 * while signed in belonged to that user, and leaving it for the next person to
 * sign in on this browser is how one account briefly renders another's data.
 */
export function useLogout() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<LogoutResponse>("/api/v1/auth/logout", {
        method: "POST",
        // An empty object rather than no body: it carries the
        // Content-Type: application/json that the server's CSRF check
        // requires on mutations.
        body: {},
      }),
    onSettled: () => {
      // onSettled, not onSuccess: if logout failed we do not know whether the
      // server destroyed the session, and keeping cached data in that state is
      // the worse of the two mistakes.
      client.clear();
    },
  });
}

/**
 * Details of a pending CLI login, for the approval screen.
 *
 * Never retried and never refetched in the background. This query is behind a
 * deliberately tight server-side rate limit — it is the endpoint that would let
 * someone grind through the user-code space — and a retry storm from an
 * honest user is indistinguishable from that at the limiter.
 */
export function useDeviceInfo(userCode: string | null) {
  return useQuery({
    queryKey: queryKeys.device(userCode ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<DeviceInfoResponse>(
        `/api/v1/device/${encodeURIComponent(userCode ?? "")}`,
        { signal },
      ),
    enabled: userCode !== null && userCode !== "",
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
  });
}

/**
 * Approves or denies a pending CLI login.
 *
 * Approving is what actually mints a long-lived credential on another machine,
 * so it is a mutation with an explicit workspace — never a default, never
 * inferred. The screen makes the user pick.
 */
export function useDeviceDecision(userCode: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      approve,
      workspaceSlug,
    }: {
      approve: boolean;
      workspaceSlug?: string;
    }) =>
      apiFetch<DeviceDecisionResponse>(
        `/api/v1/device/${encodeURIComponent(userCode)}/${approve ? "approve" : "deny"}`,
        {
          method: "POST",
          body: approve ? { workspace_slug: workspaceSlug } : {},
        },
      ),
    onSuccess: () => {
      // The code is no longer pending, so the cached lookup is now wrong, and
      // an approval adds a row to the CLI token list.
      client.invalidateQueries({ queryKey: queryKeys.device(userCode) });
      client.invalidateQueries({ queryKey: queryKeys.cliTokens });
    },
  });
}

/**
 * The repositories a workspace has uploaded results for.
 *
 * Every report is addressed by repository id, so this is the query that has to
 * resolve before any results can be fetched at all.
 */
export function useRepositories(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.repositories(slug ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<RepositoriesResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}/repositories`,
        { signal },
      ),
    enabled: slug !== undefined,
  });
}

/**
 * One repository's cells — the dataset the whole dashboard is a projection of.
 *
 * It does not poll, and that absence is protocol v3 in one line. Under the old
 * pipeline a cell landed in `stored` and a background worker moved it to
 * `ready` seconds later, with no channel to say so, so this query polled while
 * anything was in flight. Ingestion is now one transaction: by the time the
 * upload's own response has returned, every cell is complete. There is nothing
 * left to wait for, so a settled dataset makes no requests at all and a fresh
 * one is a page load away.
 *
 * A missing or foreign workspace, and a repository belonging to someone else,
 * are both 404 by design (`server/results.go`), and the client's retry policy
 * already treats that as final rather than trying twice more.
 */
export function useCells(
  slug: string | undefined,
  repoID: string | undefined,
  experiment?: string,
) {
  return useQuery({
    queryKey: queryKeys.cells(slug ?? "", repoID ?? "", experiment ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<CellsResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}` +
          `/repositories/${encodeURIComponent(repoID ?? "")}/cells` +
          (experiment ? `?experiment=${encodeURIComponent(experiment)}` : ""),
        { signal },
      ),
    enabled: slug !== undefined && repoID !== undefined,
  });
}

/**
 * One repository's configuration rollup — everything the dashboard draws.
 *
 * This replaced useCells on that screen, and the difference is not only
 * bandwidth. The rollup is computed once, at ingest, by internal/results; the
 * SPA renders it and groups nothing. A second aggregation in the browser could
 * disagree with the first — and with `berbench report` in the terminal — over
 * the same cells, and nothing would catch it.
 *
 * Like useCells it does not poll: ingestion is one transaction, so by the time
 * the upload's own response returned, the rollup was already written.
 *
 * It always asks for the all-experiments scope, and sends no `experiment`
 * parameter at all. The dashboard shows one number per configuration, averaged
 * over everything the repository has been reached by; the experiment box beside
 * the hero is an inventory rather than a filter.
 *
 * The endpoint still serves the narrowed scopes, and ingest still stores them,
 * so restoring a filter means passing the parameter again — not re-uploading.
 * If it comes back, the scope has to be part of the KEY as well as the request:
 * coverage and rank are decided by the cohort, so a narrowed cohort is a
 * different cohort, and slicing a cached unfiltered response on the client
 * would show ranks that were never computed.
 */
export function useSummary(slug: string | undefined, repoID: string | undefined) {
  return useQuery({
    queryKey: queryKeys.summary(slug ?? "", repoID ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<SummaryResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}` +
          `/repositories/${encodeURIComponent(repoID ?? "")}/summary`,
        { signal },
      ),
    enabled: slug !== undefined && repoID !== undefined,
  });
}

/**
 * One cell's trace — the only query that reads the `detail` column.
 *
 * A row at a time, deliberately. `detail` is the CLI's whole cell.json and is
 * megabytes per repository, so `useCells` does not carry it and never will;
 * the screen that draws a trajectory asks for exactly the cell it is going to
 * draw, and the server answers with a whitelist rather than the stored
 * document.
 *
 * A cell with no trace answers 200 with `trace: null`. That is a normal state
 * — an older CLI, a `script` tool, a stream no parser recognized — so the
 * caller falls back rather than treating it as an error, and this query has no
 * special error handling for it.
 */
export function useCellDetail(
  slug: string | undefined,
  repoID: string | undefined,
  cellID: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.cellDetail(slug ?? "", repoID ?? "", cellID ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<CellDetailResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}` +
          `/repositories/${encodeURIComponent(repoID ?? "")}` +
          `/cells/${encodeURIComponent(cellID ?? "")}/detail`,
        { signal },
      ),
    enabled: slug !== undefined && repoID !== undefined && cellID !== undefined,
  });
}

/**
 * Records that somebody clicked Upgrade.
 *
 * There is nothing to buy yet, so this is the only way to know whether anyone
 * would: one row per click, tagged with which button.
 *
 * Navigation-only callers fire and go: a failed POST loses one signal, while
 * blocked navigation loses the user. The activation form is the exception;
 * it uses mutate's per-call `onSuccess` to confirm that its stronger signal
 * reached the service. Nothing in the cache depends on either event because
 * an intent grants no entitlement.
 */
export function useRegisterPlanIntent(slug: string | undefined) {
  return useMutation({
    mutationFn: (body: PlanIntentRequest) =>
      apiFetch<PlanIntentResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}/plan-intents`,
        { method: "POST", body },
      ),
  });
}

/**
 * The monthly Team price to quote this user.
 *
 * The server picks the arm, persists it, and counts the user as quoted; the
 * browser only renders what it is told. `staleTime: Infinity` is not an
 * optimisation — refetching would re-render a price mid-session, and a user
 * who saw two prices is a measurement of neither.
 *
 * Callers must have something to draw when this fails. See CONTROL_PRICE.
 */
export function usePricingOffer(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.pricingOffer(slug ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<PricingOfferResponse>(
        `/api/v1/workspaces/${encodeURIComponent(slug ?? "")}/pricing-offer`,
        { signal },
      ),
    enabled: Boolean(slug),
    staleTime: Infinity,
  });
}

/** The caller's active CLI logins — one entry per `berbench login`. */
export function useCLITokens() {
  return useQuery({
    queryKey: queryKeys.cliTokens,
    queryFn: ({ signal }) =>
      apiFetch<CLITokensResponse>("/api/v1/cli/tokens", { signal }),
  });
}

/**
 * Revokes a CLI login from the browser.
 *
 * This is the button someone reaches for when a laptop is lost, which is
 * exactly when the token on it cannot be used to revoke itself. That is why
 * revocation lives on the session-authenticated side of the API.
 */
export function useRevokeCLIToken() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) =>
      apiFetch<LogoutResponse>(`/api/v1/cli/tokens/${encodeURIComponent(tokenId)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.cliTokens });
    },
  });
}
